import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getPTSByKiinteistotunnus, getPTSById, getPTSByKiinteistoId, savePTS, getLabelsByCategoryAndSection } from '../api/api';
import { useParams } from 'react-router-dom';
import { useRakennusData } from './RakennusDataContext';

const PTSContext = createContext();
export const usePTS = () => useContext(PTSContext);

export function PTSProvider({ children, onDataLoaded }) {
  const { id: kiinteistoIdd } = useParams();
  const { rakennusData } = useRakennusData()
  const kiinteistoId = rakennusData?.id_kiinteisto || kiinteistoIdd || 0;
  const [kiinteistotunnus, setKiinteistotunnus] = useState(null);
const onRemoveFromPTSRef = React.useRef([]);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
const [startYear, setStartYear] = useState(null);

const years = Array.from(
  { length: 11 },
  (_, i) => (startYear ?? new Date().getFullYear()) + i
);

  // Category-specific states
  const [tekniikkaData, setTekniikkaData] = useState([]);
  const [lviData, setLviData] = useState([]);
  const [sahkoData, setSahkoData] = useState([]);
 const [tutkimusData, setTutkimusData] = useState([

]);
  const [labelsBySection, setLabelsBySection] = useState({});

  const sumSections = (sections) => {
    const sums = Array(11).fill(0);
    sections.forEach(section => {
      section.items?.forEach(item => {
        item.values?.forEach((val, i) => {
          sums[i] += parseFloat(val) || 0;
        });
      });
    });
    return sums;
  };

  const tekniikkaYhteensa = useMemo(() => sumSections(tekniikkaData), [tekniikkaData]);
  const lviYhteensa = useMemo(() => sumSections(lviData), [lviData]);
  const sahkoYhteensa = useMemo(() => sumSections(sahkoData), [sahkoData]);
  const tutkimusYhteensa = useMemo(() => sumSections(tutkimusData), [tutkimusData]);

  const [toast, setToast] = useState({ show: false, message: '', bg: 'success' });

  // Derived PTS data & chart data
  const ptsData = useMemo(() => [
    {
      category: 'Rakennetekniikka',
      subcategories: [
        {
          name: 'Yhteenvetotaulukko',
          items: [
            { label: 'Lisätutkimukset', kl: 'KL3', values: tutkimusYhteensa },
            { label: 'Rakennetekniikka', kl: '', values: tekniikkaYhteensa },
            { label: 'LVI Järjestelmät', kl: 'KL3', values: lviYhteensa },
            { label: 'Sähköjärjestelmät', kl: '', values: sahkoYhteensa },
            {
              label: 'Yhteensä',
              kl: 'KL3',
              values: years.map((_, i) =>
                (parseFloat(tekniikkaYhteensa[i]) || 0) +
                (parseFloat(lviYhteensa[i]) || 0) +
                (parseFloat(sahkoYhteensa[i]) || 0) +
                (parseFloat(tutkimusYhteensa[i]) || 0)
              ),
            },
          ],
        },
      ],
    },
  ], [tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa, years]);

  const chartData = useMemo(() => years.map((year, idx) => {
    const sub = ptsData[0]?.subcategories?.[0];
    if (!sub) return { year };
    const row = { year };
    sub.items.forEach(item => {
      if (item.label !== 'Yhteensä') row[item.label] = parseFloat(item.values[idx]) || 0;
    });
    return row;
  }), [ptsData, years]);

  const footerTotals = useMemo(() => {
    return years.map((_, yearIdx) => {
      return (
        (parseFloat(tekniikkaYhteensa[yearIdx]) || 0) +
        (parseFloat(lviYhteensa[yearIdx]) || 0) +
        (parseFloat(sahkoYhteensa[yearIdx]) || 0) +
        (parseFloat(tutkimusYhteensa[yearIdx]) || 0)
      );
    });
  }, [
    years,
    tekniikkaYhteensa,
    lviYhteensa,
    sahkoYhteensa,
    tutkimusYhteensa
  ]);
  const availablePTSSections = useMemo(() => {
    const map = {};

    const add = (category, sections) => {
      if (!sections?.length) return;
      map[category] = sections
        .map(s => s.name || s.header)
        .filter(Boolean);
    };

    add("Rakennetekniikka", tekniikkaData);
    add("LVI Järjestelmät", lviData);
    add("Sähköjärjestelmät", sahkoData);
   add("Lisätutkimukset", tutkimusData.length ? tutkimusData : [
  { name: "Tutkimustarpeet" }
]);

  return map;
}, [tekniikkaData, lviData, sahkoData, tutkimusData]);

  // ===================== Table Update Functions =====================

  const updateTableValue = (category, sectionIdx, itemIdx, yearIdx, value) => {
    const updater = { 'Rakennetekniikka': setTekniikkaData, 'LVI Järjestelmät': setLviData, 'Sähköjärjestelmät': setSahkoData, 'Lisätutkimukset': setTutkimusData }[category];
    const data = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData, 'Lisätutkimukset': tutkimusData }[category];
    const copy = [...data];
    copy[sectionIdx].items[itemIdx].values[yearIdx] = value;
    updater(copy);
  };

  const updateTableLabel = (category, sectionIdx, itemIdx, value) => {
    const updater = { 'Rakennetekniikka': setTekniikkaData, 'LVI Järjestelmät': setLviData, 'Sähköjärjestelmät': setSahkoData, 'Lisätutkimukset': setTutkimusData }[category];
    const data = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData, 'Lisätutkimukset': tutkimusData }[category];
    const copy = [...data];
    copy[sectionIdx].items[itemIdx].label = value;
    updater(copy);
  };

  const updateTableKL = (category, sectionIdx, itemIdx, value) => {
    const updater = { 'Rakennetekniikka': setTekniikkaData, 'LVI Järjestelmät': setLviData, 'Sähköjärjestelmät': setSahkoData, 'Lisätutkimukset': setTutkimusData }[category];
    const data = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData, 'Lisätutkimukset': tutkimusData }[category];
    const copy = [...data];
    copy[sectionIdx].items[itemIdx].kl = value;
    updater(copy);
  };

  const addTableRow = (category, sectionIdx) => {
    const updater = { 'Rakennetekniikka': setTekniikkaData, 'LVI Järjestelmät': setLviData, 'Sähköjärjestelmät': setSahkoData, 'Lisätutkimukset': setTutkimusData }[category];
    const data = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData, 'Lisätutkimukset': tutkimusData }[category];
    const copy = [...data];
    copy[sectionIdx].items.push({ label: '', kl: '', values: Array(11).fill('') });
    updater(copy);
  };

  const insertTableRow = (category, sectionIdx, rowData) => {
    const setterMap = { 'Rakennetekniikka': setTekniikkaData, 'LVI Järjestelmät': setLviData, 'Sähköjärjestelmät': setSahkoData };
    const dataMap = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData };
    const updater = setterMap[category];
    const data = dataMap[category];
    if (!updater || !data) return;
    const copy = structuredClone(data);
    copy[sectionIdx].items.push({
      label: rowData.label || '',
      kl: rowData.kl || '',
      values: rowData.values?.length === 11 ? [...rowData.values] : Array(11).fill('')
    });
    updater(copy);
  };

const removeTableRow = (category, sectionIdx, itemIdx) => {
  const updater = {
    'Rakennetekniikka': setTekniikkaData,
    'LVI Järjestelmät': setLviData,
    'Sähköjärjestelmät': setSahkoData,
    'Lisätutkimukset': setTutkimusData
  }[category];

  const data = {
    'Rakennetekniikka': tekniikkaData,
    'LVI Järjestelmät': lviData,
    'Sähköjärjestelmät': sahkoData,
    'Lisätutkimukset': tutkimusData
  }[category];

  const copy = [...data];

  const removedItem = copy[sectionIdx].items[itemIdx];
console.log("REMOVING FROM PTS:", removedItem);
console.log("CALLBACK EXISTS?", onRemoveFromPTSRef.current);
  copy[sectionIdx].items.splice(itemIdx, 1);
  updater(copy);

  // 🔥 THIS IS THE MISSING LINK
onRemoveFromPTSRef.current.forEach(cb => {
  cb({
    id: removedItem?.metadata?.toimenpideId,
    label: removedItem?.label,
  });
});}

  // ===================== Fetch PTS Data =====================
  useEffect(() => {
    if (!kiinteistoId) return;

    const fetchPTS = async () => {
      try {
        const listRes = await getPTSByKiinteistoId(kiinteistoId);
        const ptsList = listRes.data;
        if (!ptsList.length) {
          onDataLoaded?.({ hasPTSData: false });
          return;
        }

        setKiinteistotunnus(ptsList[0]?.kiinteistotunnus);
        const fullRes = await getPTSById(ptsList[0].id);
        console.log(fullRes.data);
        const fullPTS = fullRes.data;

          const createdAt = new Date(fullPTS.project.created_at);

          const reportStartYear =
            createdAt.getMonth() >= 7
              ? createdAt.getFullYear() + 1
              : createdAt.getFullYear();

          setStartYear(reportStartYear);

          console.log({
            created_at: fullPTS.project.created_at,
            month: createdAt.getMonth(),
            reportStartYear
          });


        
        const entries = fullPTS.entries || [];

        const normalizeCategoryName = (name) => {
          switch (name) {
            case "LVI-tekniikka": return "LVI Järjestelmät";
            case "Sähkötekniikka": return "Sähköjärjestelmät";
            default: return name;
          }
        };

        const filterByCategory = (cat) =>
          entries.filter(e => normalizeCategoryName(e.category) === cat);

        const createDefaultSections = (category) => {
  const templates = {
    "Rakennetekniikka": [
      "Vierustat ja kuivatusosat",
      "Pihapäällysteet",
      "Aluevarusteet ja -rakenteet",
      "Perustukset ja sokkelit",
      "Alapohja",
      "Rakennusrunko",
      "Ulkoseinät",
      "Ikkunat",
      "Ulko-ovet",
      "Parvekkeet",
      "Kattorakenteet",
      "Sisätilat",
      "Märkätilat"
    ],
    "LVI Järjestelmät": [
      "Lämmöntuotanto",
      "Lämmitysverkosto",
      "Vesijohtoverkosto",
      "Viemäriverkosto",
      "Ilmanvaihtojärjestelmä",
      "Jäähdytysjärjestelmät",
      "Rakennusautomaatio",
      "Muut järjestelmät"
    ],
    "Sähköjärjestelmät": [
      "Aluesähköistys",
      "Kytkinlaitokset ja jakokeskukset",
      "Johdot ja niiden varusteet",
      "Valaisimet, lämmittimet, kojeet ja laitteet",
      "Tele- ja antennijärjestelmät",
      "Palo- ja turvajärjestelmät",
      "Siirtolaitteet"
    ],
    "Lisätutkimukset": [
      "Tutkimustarpeet"
    ]
  };

  return (templates[category] || []).map(name => ({ name }));
};

const mapToSections = (entries, defaultSections) => {
  const grouped = {};

  entries.forEach(e => {
    const sectionName = e.section;
    grouped[sectionName] = grouped[sectionName] || [];

    const values = Array.from({ length: 11 }, (_, i) => {
      const raw = e.values_by_year?.[`y${i + 1}`];
      return raw === undefined || raw === null ? "" : raw;
    });

    grouped[sectionName].push({
      id: e.id,
      label: e.label || '',
      kl: e.kl_rating || '',
      values,
      metadata: {
  ...e.metadata,
 toimenpideId: e.metadata?.toimenpideId || null
}
    });
  });

  // 🔥 ALWAYS return ALL sections
  return defaultSections.map(section => ({
    name: section.name,
    items: grouped[section.name] || [
      { label: "", kl: "", values: Array(11).fill("") }
    ]
  }));
};

  const tekniikka = mapToSections(
  filterByCategory("Rakennetekniikka"),
  createDefaultSections("Rakennetekniikka")
);

const lvi = mapToSections(
  filterByCategory("LVI Järjestelmät"),
  createDefaultSections("LVI Järjestelmät")
);

const sahko = mapToSections(
  filterByCategory("Sähköjärjestelmät"),
  createDefaultSections("Sähköjärjestelmät")
);

const tutkimus = mapToSections(
  filterByCategory("Lisätutkimukset"),
  createDefaultSections("Lisätutkimukset")
);

        setTekniikkaData(tekniikka);
        setLviData(lvi);
        setSahkoData(sahko);
      setTutkimusData(
  tutkimus.length
    ? tutkimus
    : [
        {
          name: "Tutkimustarpeet",
          items: [{ label: "", kl: "", values: Array(11).fill("") }]
        }
      ]
);

        onDataLoaded?.({ hasPTSData: true });
      } catch (err) {
        console.error("❌ Virhe ladattaessa PTS-tietoja:", err);
      }
    };

    fetchPTS();
  }, [kiinteistoId]);

  // ===================== Fetch labels by section =====================
  const fetchLabelsForCategory = async (category) => {
    const dataMap = { 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData, 'Lisätutkimukset': tutkimusData };
    if (!category || !dataMap[category]?.length) return;

    try {
      const results = await Promise.all(
        dataMap[category].map(section => getLabelsByCategoryAndSection(category, section.name))
      );
      const map = {};
      dataMap[category].forEach((section, idx) => {
        map[section.name] = results[idx].data.labels;
      });
      setLabelsBySection(prev => ({ ...prev, [category]: map }));
    } catch (err) {
      console.error('❌ Virhe ladattaessa labels:', err);
    }
  };

  // ===================== Save =====================
 const handleSavePTS = async (category) => {
  if (!kiinteistoId) return;

  const flattenForPTS = (sections, category) =>
    sections.flatMap(section =>
      (section.items || []).map(item => ({
        id: item.id ?? undefined,
        category,
        section: section.name || section.header || '',
        label: item.label || '',
        kl_rating: item.kl || '',
        values_by_year: (item.values || []).reduce((acc, val, idx) => {
          acc[`y${idx + 1}`] =
            val === undefined || val === null || String(val).trim() === ""
              ? "0"
              : String(val);
          return acc;
        }, {}),
       metadata: item.metadata || {}
      }))
    );

  const dataMap = {
    'Rakennetekniikka': tekniikkaData,
    'LVI Järjestelmät': lviData,
    'Sähköjärjestelmät': sahkoData,
    'Lisätutkimukset': tutkimusData
  };

  const payload = {
    kiinteistotunnus,
    title: 'PTS Raportti',
    created_by: 'UI',
    entries: flattenForPTS(dataMap[category], category)
  };

   try {
    const listRes = await getPTSByKiinteistoId(kiinteistoId);
    const ptsList = listRes.data;
    const existingPTS = ptsList?.[0];

    await savePTS(payload, existingPTS?.id);

    setToast({
      show: true,
      message: "✅ PTS muutokset tallennettu",
      bg: "success"
    });

  } catch (err) {
    console.error(err);

    setToast({
      show: true,
      message: "❌ PTS tallennus epäonnistui",
      bg: "danger"
    });
  }
};

  ///Autosave

 useEffect(() => {
  if (!kiinteistoId) return;

  const timeout = setTimeout(() => {
    handleSavePTS("Rakennetekniikka");
  }, 1000);

  return () => clearTimeout(timeout);
}, [tekniikkaData]);

useEffect(() => {
  if (!kiinteistoId) return;

  const timeout = setTimeout(() => {
    handleSavePTS("LVI Järjestelmät");
  }, 1000);

  return () => clearTimeout(timeout);
}, [lviData]);

useEffect(() => {
  if (!kiinteistoId) return;

  const timeout = setTimeout(() => {
    handleSavePTS("Sähköjärjestelmät");
  }, 1000);

  return () => clearTimeout(timeout);
}, [sahkoData]);

useEffect(() => {
  if (!kiinteistoId) return;

  const timeout = setTimeout(() => {
    handleSavePTS("Lisätutkimukset");
  }, 1000);

  return () => clearTimeout(timeout);
}, [tutkimusData]);

  const defaultPTSContext = {
    years: [],
    currentYear: null,
    currentMonth: null,
    startYear: null,
    tekniikkaData: [],
    lviData: [],
    sahkoData: [],
    tutkimusData: [],
    tekniikkaYhteensa: [],
    lviYhteensa: [],
    sahkoYhteensa: [],
    tutkimusYhteensa: [],
    ptsData: [],
    chartData: [],
    footerTotals: [],
    toast: { show: false, message: '', bg: 'success' },
    setToast: () => {},
    updateTableValue: () => {},
    updateTableLabel: () => {},
    updateTableKL: () => {},
    addTableRow: () => {},
    removeTableRow: () => {},
    insertTableRow: () => {},
    fetchLabelsForCategory: () => {},
    labelsBySection: {},
    handleSavePTS: () => {},
    availablePTSSections: {}, 
  };

  return (
      <PTSContext.Provider
        value={
          kiinteistoId
            ? {
                years,
                currentYear,
                currentMonth,
                startYear,
                tekniikkaData, setTekniikkaData,
                lviData, setLviData,
                sahkoData, setSahkoData,
                tutkimusData, setTutkimusData,
                tekniikkaYhteensa,
                lviYhteensa,
                sahkoYhteensa,
                tutkimusYhteensa,
                ptsData,
                chartData,
                footerTotals,
                toast, setToast,
                updateTableValue,
                updateTableLabel,
                updateTableKL,
                addTableRow,
                removeTableRow,
                insertTableRow,
               onRemoveFromPTS: onRemoveFromPTSRef,
                fetchLabelsForCategory,
                labelsBySection,
                handleSavePTS,
                availablePTSSections, // you can fill real data when loaded
              }
            : defaultPTSContext
        }
      >
        {children}
      </PTSContext.Provider>
  );
}
